import { faLocationArrow, faMap } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DialogContentText } from "@mui/material";
import { notification } from "antd";
import React from "react";
import GlobalStoreContext from "../../../context/GlobalStoreContext";
import Helper from "../../../Helper";
import { CalendarEvent } from "../../../models/CalendarEvent";
import { CalendarEventResource } from "../../../models/CalendarEventResource";
import { HaulRoute } from "../../../models/HaulRoute";
import { MapObject } from "../../../models/MapObject";
import AreaAvatar from "../../Map/AreaAvatar";
import DamnMap from "../damnMap";
import DamnDirectionsModal from "./damnDirectionsModal";
import DropoffSection from "./dropoffSection";
import GateSection from "./gateSection";
import GateToDropoffRouteSection from "./gateToDropoffRouteSection";
import HaulRouteSection from "./haulRouteSection";
import { DamnMapItems } from "./useDamnMapItems";

interface DamnMapSectionProps {
    calendarEvent: CalendarEvent;
    setCalendarEvent: (calendarEvent: CalendarEvent) => void;
    mapItems: DamnMapItems;
}

const DamnMapSection: React.FC<DamnMapSectionProps> = (props) => {
    const context = React.useContext(GlobalStoreContext);

    const {
        project,
        dropoff,
        dropOffMapObject,
        gate,
        gateMapObject,
        gateToDropoffRoutes,
        haulRoutes,
        selectedHaulRouteId,
        selectedHaulRoute,
        selectedHaulRouteMapObject,
        mapObjects,
    } = props.mapItems;

    const [damnDirectionsModal, setDamnDirectionsModal] = React.useState<boolean>(false);
    const [savingHaulRoute, setSavingHaulRoute] = React.useState<boolean>(false);

    // Directions end at the delivery's gate when it has one, otherwise straight to the dropoff with no stops on the way.
    const directionsDestination = gateMapObject || dropOffMapObject;

    const handleMapDirections = () => {
        if (selectedHaulRouteMapObject) {
            setDamnDirectionsModal(true);
            return;
        }
        if (directionsDestination) {
            Helper.openMapLink(directionsDestination);
        }
    };

    const selectHaulRoute = (haulRoute: HaulRoute) => {
        if (!project || haulRoute.id === selectedHaulRouteId) {
            return;
        }

        setSavingHaulRoute(true);

        const newCalendarEvent = Object.create(props.calendarEvent) as CalendarEvent;

        const newResources =
            newCalendarEvent.calendarEventResources?.map(
                (resource) => Object.create(resource) as CalendarEventResource
            ) || [];

        const existingHaulRouteResources = newResources.filter((resource) => resource.deliveryRole === "haul_route");

        if (existingHaulRouteResources.length > 0) {
            // Update the already-assigned resource in place rather than destroying it and creating a
            // new one - simpler, and avoids any doubt about destroy+create ordering within a single
            // nested-attributes update.
            existingHaulRouteResources[0].calendarResourceableId = haulRoute.id;

            // If more than one haul_route resource somehow already exists (e.g. left over from a past
            // bug), destroy the extras so we only ever end up with one.
            existingHaulRouteResources.slice(1).forEach((resource) => {
                if (resource.id) {
                    resource._destroy = true;
                }
            });
        } else {
            newResources.push(
                CalendarEventResource.fromApiObject(
                    {
                        calendar_resourceable_id: haulRoute.id,
                        calendar_resourceable_type: "HaulRoute",
                        consumes_availability: false,
                        delivery_role: "haul_route",
                        participation_status: "accepted",
                    },
                    project.timezone
                )
            );
        }

        newCalendarEvent.calendarEventResources = newResources;

        context
            .updateCalendarEvent(newCalendarEvent)
            .then((updated) => {
                if (!updated) {
                    throw new Error("Unable to update haul route");
                }

                props.setCalendarEvent(updated);
            })
            .catch(() => {
                notification.error({
                    message: "Unable to Update Haul Route",
                    description: "Please try again.",
                });
            })
            .finally(() => {
                setSavingHaulRoute(false);
            });
    };

    const handleMapObjectClick = (mapObject: MapObject) => {
        if (savingHaulRoute) return;

        const mappable = mapObject.getMappable(context);
        if (mappable instanceof HaulRoute) {
            selectHaulRoute(mappable);
        }
    };

    if (mapObjects.length === 0) {
        return null;
    }

    const verifyMapObjects = () => {
        if (!mapObjects || mapObjects.length === 0) {
            return false;
        }

        return true;
    };

    const deliveryDirectionsModalText = () => (
        <>
            <DialogContentText>This delivery has a preapproved haul route to follow to the jobsite.</DialogContentText>
            <DialogContentText>
                Click Get Directions below to navigate to the start of{" "}
                <div className="flex flex-row items-center font-semibold">
                    {selectedHaulRouteMapObject && (
                        <AreaAvatar mappable={selectedHaulRoute} mapObject={selectedHaulRouteMapObject} />
                    )}{" "}
                    {selectedHaulRoute?.name}
                </div>
                {gateMapObject && gate ? (
                    <>
                        and then on to the jobsite at{" "}
                        <div className="flex flex-row items-center font-semibold">
                            {gateMapObject && <AreaAvatar mappable={gate} mapObject={gateMapObject} />} {gate.name}
                        </div>
                    </>
                ) : (
                    dropoff && (
                        <>
                            and then on to the dropoff at{" "}
                            <div className="flex flex-row items-center font-semibold">
                                <AreaAvatar mappable={dropoff} /> {dropoff.name}
                            </div>
                        </>
                    )
                )}
            </DialogContentText>
        </>
    );

    return (
        <section className="mt-4 xl:mx-4">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200/50">
                <div className="p-5 pb-4 bg-linear-to-r from-gray-50 to-white">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex gap-2 items-center">
                            <FontAwesomeIcon icon={faMap} className="text-plot-orange" />
                            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Navigation</h2>
                        </div>

                        <button
                            type="button"
                            onClick={handleMapDirections}
                            className="cursor-pointer bg-linear-to-r from-plot-orange to-plot-orange/90 hover:from-plot-orange/90 hover:to-plot-orange text-white font-bold p-3 md:p-4 rounded-xl flex items-center justify-center gap-3 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                        >
                            <FontAwesomeIcon icon={faLocationArrow} />
                            Get Directions
                        </button>
                    </div>
                </div>

                {verifyMapObjects() && (
                    <div className="relative w-full h-80 bg-linear-to-br from-gray-100 to-gray-200">
                        <DamnMap
                            mapId="damnMap"
                            mapObjects={mapObjects}
                            geoControlRef={context.geoControlRef}
                            setUserLocation={context.setUserLocation}
                            project={project}
                            route={selectedHaulRoute}
                            stepRoutes={gateToDropoffRoutes}
                            onMapObjectClick={handleMapObjectClick}
                        />
                    </div>
                )}

                {/* Navigation Info & Button */}
                <div className="mt-5 flex flex-col gap-4 w-full">
                    <HaulRouteSection
                        haulRoutes={haulRoutes}
                        selectedHaulRoute={selectedHaulRoute}
                        selectedHaulRouteId={selectedHaulRouteId}
                        saving={savingHaulRoute}
                        onSelect={selectHaulRoute}
                        gateToDropoffRoutes={gateToDropoffRoutes}
                        dropoff={dropoff}
                    />
                    <GateSection gate={gate} gateMapObject={gateMapObject} />

                    <GateToDropoffRouteSection routes={gateToDropoffRoutes} dropoff={dropoff} />

                    <DropoffSection dropoff={dropoff} dropoffMapObject={dropOffMapObject} />
                </div>
            </div>
            <DamnDirectionsModal
                open={damnDirectionsModal}
                toggleOpen={() => setDamnDirectionsModal(!damnDirectionsModal)}
                handleMapDirections={() => {
                    // Via the start of the haul route.
                    const destination = directionsDestination || selectedHaulRouteMapObject;
                    if (!destination) return;

                    const waypoints = [selectedHaulRouteMapObject].filter(
                        (mapObject): mapObject is MapObject => mapObject !== null && mapObject !== destination
                    );

                    Helper.openMapLink(destination, waypoints);
                }}
                body={deliveryDirectionsModalText()}
            />
        </section>
    );
};

export default DamnMapSection;
